import { Controller } from '@hotwired/stimulus'

// Bascule Suivre l'appareil / Clair / Sombre : l'écran change au clic, le choix est
// enregistré ensuite sur l'appareil (cookie, cf. Themable) en envoyant le formulaire de
// l'option. Si l'enregistrement échoue, l'écran revient au thème d'avant : sinon il
// repartirait dans l'ancien au chargement suivant, sans prévenir.
//
// Dans l'app, sans fondu : la barre native change d'un coup. Sur Android, l'app est prévenue
// au clic, sans attendre l'enregistrement, et la page prend ses couleurs à sa réponse
// (bridge/theme_controller), sous le fondu natif de tout l'écran. Si l'enregistrement
// échoue, l'app est ramenée au thème d'avant.
export default class extends Controller {
  static targets = ['option', 'thumb']

  select(event) {
    event.preventDefault()
    const form = event.currentTarget
    const { theme } = event.params
    const previous = this.#current()
    if (this.#android) return this.#selectOnAndroid(form, theme, previous)

    this.#switch(theme)

    fetch(form.action, { method: form.method, body: new FormData(form), headers: { Accept: 'application/json' } })
      .then((response) => { if (!response.ok) this.#switch(previous) })
      .catch(() => this.#switch(previous))
  }

  #selectOnAndroid(form, theme, previous) {
    this.#apply(theme, { colors: false })
    this.dispatch('changed', { detail: { theme } })
    const revert = () => {
      this.#apply(previous, { colors: false })
      this.dispatch('changed', { detail: { theme: previous } })
    }

    fetch(form.action, { method: form.method, body: new FormData(form), headers: { Accept: 'application/json' } })
      .then((response) => { if (!response.ok) revert() })
      .catch(revert)
  }

  #current() {
    return this.optionTargets.find((option) => option.getAttribute('aria-pressed') === 'true')?.dataset.themeThemeParam || 'system'
  }

  // Fondu de la page entière plutôt qu'une transition par élément, qui se
  // désynchronisent et donnent un clignotement.
  #switch(theme) {
    const root = document.documentElement
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches

    // Dans l'app, la barre native change d'un coup : la page aussi, sans fondu.
    if (!document.startViewTransition || reduced || this.#native) return this.#apply(theme)

    root.setAttribute('data-theme-switching', '')

    document
      .startViewTransition(() => this.#apply(theme))
      .finished.finally(() => root.removeAttribute('data-theme-switching'))
  }

  #apply(theme, { colors = true } = {}) {
    const root = document.documentElement

    window.Turbo?.cache?.clear() // les captures portent l'ancien thème

    if (theme === 'system') {
      if (colors) root.removeAttribute('data-theme')
      delete document.body.dataset.themePreference
    } else {
      if (colors) root.setAttribute('data-theme', theme)
      document.body.dataset.themePreference = theme
    }

    this.optionTargets.forEach((option, index) => {
      const selected = option.dataset.themeThemeParam === theme
      option.setAttribute('aria-pressed', String(selected))
      if (selected) this.thumbTarget.style.transform = `translateX(${index * 100}%)`
    })

    if (!colors) return
    this.#applyThemeColor(theme)
    // Dans l'app iOS, bridge/theme_controller fait suivre le choix au chrome natif.
    if (!this.#android) this.dispatch('changed', { detail: { theme } })
  }

  get #native() {
    return document.body.matches('[data-controller~="bridge--theme"]')
  }

  get #android() {
    return document.body.classList.contains('hotwire-native-android')
  }

  // La barre du navigateur mobile suit <meta name="theme-color"> : même règle que
  // layouts/_head, une couleur par thème, les deux avec leur media pour « Suivre
  // l'appareil ». La couleur vient de la feuille de style, pas recopiée ici.
  #applyThemeColor(theme) {
    const head = document.head
    head.querySelectorAll('meta[name="theme-color"]').forEach((meta) => meta.remove())
    const background = () => getComputedStyle(document.documentElement).getPropertyValue('--color-gray-0').trim()

    const add = (content, media) => {
      const meta = document.createElement('meta')
      meta.name = 'theme-color'
      meta.content = content
      if (media) meta.media = media
      head.append(meta)
    }

    if (theme === 'system') {
      // Les deux couleurs lues en posant chaque thème le temps d'une lecture.
      const root = document.documentElement
      root.setAttribute('data-theme', 'light')
      const light = background()
      root.setAttribute('data-theme', 'dark')
      const dark = background()
      root.removeAttribute('data-theme')
      add(light, '(prefers-color-scheme: light)')
      add(dark, '(prefers-color-scheme: dark)')
    } else {
      add(background())
    }
  }
}
